import { useEffect, useRef } from 'react';
import './urban-network-loader.css';

/**
 * UrbanNetworkLoaderV4 — nodos + íconos juntos → segmentos → íconos salen → W + highlight → ciudad → fade → loop
 *
 * Secuencia:
 *   NODES_ICONS_IN — 30 nodos + íconos aparecen juntos con bounce escalonado
 *   EDGES_IN       — segmentos se tejen entre nodos
 *   ICONS_OUT      — íconos desaparecen con bounce (delays aleatorios); nodos permanecen
 *   HOLD_URBAN     — trama urbana sola
 *   CONVERGE_FWD   — ciudad → W (5 s)
 *   HOLD_W         — W estática + highlight/brillo en el perímetro al formarse
 *   CONVERGE_REV   — W → ciudad (5 s)
 *   NET_FADEOUT    — ciudad se disuelve
 *   ↩ loop (canvas en negro → nodos + íconos aparecen de nuevo)
 */

export interface UrbanNetworkLoaderV4Props {
  size?: 'xs' | 'sm' | 'md' | 'xl';
  className?: string;
}

// ── Timing ────────────────────────────────────────────────────────────────────
const TIMING_FULL = {
  FADE_IN:        300,
  NODES_ICONS_IN: 1000,
  EDGES_IN:        900,
  ICONS_OUT:       700,
  HOLD_URBAN:      400,
  CONVERGE:       5000,
  HOLD_W:         1200,
  NET_FADEOUT:     700,
};
const TIMING_COMPACT = {
  FADE_IN:        200,
  NODES_ICONS_IN:  650,
  EDGES_IN:        600,
  ICONS_OUT:       450,
  HOLD_URBAN:      250,
  CONVERGE:       2000,
  HOLD_W:          800,
  NET_FADEOUT:     450,
};

// ── Posiciones finales — W alámbrica ──────────────────────────────────────────
const FP: [number, number][] = [
  [472.5,  853.5 ], [382.5,  695    ], [581,    686.5  ], [433.25, 608.5  ], [484,    522    ],
  [371,    672    ], [472.5,  506    ], [363,    485.5  ], [268.75, 485.5  ], [355,    299    ],
  [166.5,  299    ], [496.5,  506    ], [589.5,  672    ], [627,    602    ], [627,    299    ],
  [695.5,  402.5  ], [669.5,  672    ], [764,    506    ],
  [787.256,854    ], [678.847,686.849], [778.91, 613    ], [832.218,774.679],
  [877.181,695.357], [775.766,522.201], [787.256,506.187],
  [888.671,672.336], [939.753,579.002], [904.658,299    ],
  [990.836,485.668], [1093,   299    ],
];

// ── Posiciones iniciales — ciudad radial ──────────────────────────────────────
const UP: [number, number][] = [
  [185,  568],[340,  552],[510,  545],[695,  552],[865,  545],[1072, 562],
  [572,  372],[792,  398],[912,  548],[852,  698],[645,  778],[432,  752],
  [322,  615],[372,  428],[528,  462],[668,  448],[738,  615],[648,  672],
  [525,  652],[462,  548],[248,  385],[188,  490],[205,  668],[288,  818],
  [462,  838],[932,  388],[1065, 468],[1088, 658],[948,  778],[742,  832],
];

// ── Aristas urbanas ───────────────────────────────────────────────────────────
const UE: [number, number][] = [
  [0,1],[1,2],[2,3],[3,4],[4,5],
  [6,7],[7,8],[8,9],[9,10],[10,11],[11,12],[12,13],[13,6],
  [1,13],[1,12],[4,8],
  [14,15],[15,16],[16,17],[17,18],[18,19],[19,14],
  [2,14],[3,15],[6,14],[9,17],[11,18],[12,19],
  [13,20],[12,21],[11,23],[10,24],[7,25],[8,26],[9,28],[10,29],
  [20,21],[21,22],[25,26],[26,27],[27,28],
];

// ── Aristas W ─────────────────────────────────────────────────────────────────
const EI: [number, number][] = [
  [0,1],[0,2],[0,3],[1,3],[1,2],[2,4],[4,3],[2,3],
  [5,6],[5,7],[5,8],[6,9],[6,7],[9,10],[9,7],[10,7],[10,8],[7,8],
  [11,12],[12,13],[11,14],[11,15],[11,13],[11,17],
  [13,16],[16,17],[13,17],[13,14],[17,15],[14,15],
  [18,19],[18,20],[18,21],[22,23],[22,21],[22,20],
  [23,19],[23,20],[19,20],[19,21],
  [24,25],[25,26],[24,27],[24,28],[24,26],[24,29],
  [27,29],[27,28],[29,28],[28,26],
];

// ── Radio de nodos ────────────────────────────────────────────────────────────
const OUTER_R = [12,12,12,11,11,12,12,11,11,12,14,12,11,12,14,11,11,12,14,12,11,12,12,11,12,11,11,12,11,14];
const INNER_R = [6,5.5,5.5,5,5,5.5,5.5,5,5,5.5,7,5.5,5,5.5,7,5,5,5.5,7,5.5,5,5.5,5.5,5,5.5,5,5,5.5,5,7];

// ── Íconos — uno por nodo (30 total) ─────────────────────────────────────────
type IconType = 'house' | 'tree' | 'bin' | 'bus' | 'lamp';

// Distribución con variedad sin patrón demasiado evidente
const ICON_FOR_NODE: IconType[] = [
  'house', 'tree',  'bin',   'bus',  'lamp',   // 0–4
  'tree',  'lamp',  'house', 'bin',  'bus',    // 5–9
  'lamp',  'bin',   'tree',  'bus',  'house',  // 10–14
  'tree',  'house', 'lamp',  'bin',  'bus',    // 15–19
  'house', 'lamp',  'tree',  'bin',  'bus',    // 20–24
  'tree',  'bin',   'house', 'lamp', 'bus',    // 25–29
];

const ICON_Y_OFFSET = 50;   // debajo del nodo
const ICON_SIZE     = 0.52; // ligeramente más pequeños que los del V3

// ── Easing ────────────────────────────────────────────────────────────────────
function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
// Aparición con rebote suave: crece rápido → se achica levemente → vuelve a normal
function bounceSettle(t: number): number {
  if (t < 0.35) { const u = t / 0.35; return 1 - Math.pow(1 - u, 3); }
  if (t < 0.55) { const u = (t - 0.35) / 0.20; return 1 - 0.18 * (3*u*u - 2*u*u*u); }
  const u = (t - 0.55) / 0.45; return 0.82 + 0.18 * (3*u*u - 2*u*u*u);
}
// Desaparición con rebote: suaviza levemente → salta → colapsa
function bounceOut(t: number): number {
  if (t < 0.20) { const u = t / 0.20; return 1 - 0.18 * (3*u*u - 2*u*u*u); }
  if (t < 0.45) { const u = (t - 0.20) / 0.25; return 0.82 + 0.26 * (3*u*u - 2*u*u*u); }
  const u = (t - 0.45) / 0.55; return Math.max(0, 1.08 * (1 - u*u*u));
}

// ── Renderizado de íconos ─────────────────────────────────────────────────────
function IconPaths({ type }: { type: IconType }): JSX.Element {
  switch (type) {
    case 'house': return (<>
      <path d="M0,-38 L28,-6 L28,30 L-28,30 L-28,-6 Z" />
      <path d="M-10,30 L-10,8 L10,8 L10,30" />
    </>);
    case 'tree': return (<>
      <ellipse cx="0" cy="-12" rx="20" ry="25" />
      <line x1="0" y1="13" x2="0" y2="30" strokeWidth={6} />
    </>);
    case 'bin': return (<>
      <path d="M-18,-5 L-14,32 L14,32 L18,-5 Z" />
      <line x1="-21" y1="-5" x2="21" y2="-5" />
      <path d="M-7,-5 L-5,-20 Q0,-27 5,-20 L7,-5" />
    </>);
    case 'bus': return (<>
      <rect x="-36" y="-18" width="72" height="32" rx="5" />
      <circle cx="-22" cy="14" r="7" />
      <circle cx="22" cy="14" r="7" />
      <line x1="-8" y1="-18" x2="-8" y2="14" />
      <line x1="8" y1="-18" x2="8" y2="14" />
    </>);
    case 'lamp': return (<>
      <line x1="0" y1="30" x2="0" y2="-18" />
      <path d="M0,-18 Q0,-40 -18,-40" fill="none" />
      <circle cx="-18" cy="-40" r="8" />
      <line x1="-18" y1="-32" x2="-18" y2="-27" strokeOpacity={0.65} />
      <line x1="-24" y1="-35" x2="-27" y2="-31" strokeOpacity={0.65} />
      <line x1="-26" y1="-40" x2="-32" y2="-40" strokeOpacity={0.65} />
      <line x1="-24" y1="-45" x2="-27" y2="-49" strokeOpacity={0.65} />
    </>);
  }
}

// ── Componente ────────────────────────────────────────────────────────────────
export function UrbanNetworkLoaderV4({
  size = 'md',
  className,
}: UrbanNetworkLoaderV4Props): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const cls = ['unl', `unl--${size}`, className].filter(Boolean).join(' ');
  const hideUE    = size === 'xs';
  const hideIcons = size === 'xs';

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const T = size === 'sm' || size === 'xs' ? TIMING_COMPACT : TIMING_FULL;
    const LOOP_DUR =
      T.NODES_ICONS_IN + T.EDGES_IN + T.ICONS_OUT + T.HOLD_URBAN +
      T.CONVERGE + T.HOLD_W + T.CONVERGE + T.NET_FADEOUT;

    const UE_FADE_END    = 0.55;
    const EI_FADE_START  = 0.30;
    const STAGGER_MAX    = 0.44;   // delay máximo de entrada de cada nodo/ícono
    const ANIM_WINDOW    = 1 - STAGGER_MAX;  // ventana de animación por elemento
    const EDGE_MAX_DELAY = 0.58;
    const EDGE_ANIM_DUR  = 1 - EDGE_MAX_DELAY;
    const EXIT_MAX_DELAY = 0.55;
    const EXIT_ANIM_DUR  = 1 - EXIT_MAX_DELAY;

    // Elementos del DOM
    const ueLines    = svg.querySelectorAll<SVGLineElement>('.unl__ue');
    const elLines    = svg.querySelectorAll<SVGLineElement>('.unl__el');
    const elHiLines  = svg.querySelectorAll<SVGLineElement>('.unl__el-hi');
    const noCircles  = svg.querySelectorAll<SVGCircleElement>('.unl__no');
    const niCircles  = svg.querySelectorAll<SVGCircleElement>('.unl__ni');
    const iconGroups = !hideIcons ? svg.querySelectorAll<SVGGElement>('.unl__icon') : null;

    // Buffers de animación
    const iconScales    = new Array<number>(30).fill(0);
    const nodeIconDelay = new Array<number>(30).fill(0);
    const edgeDelay     = new Array<number>(UE.length).fill(0);
    const exitDelay     = new Array<number>(30).fill(0);

    let nodeIconsInit = false;
    let edgesInit     = false;
    let exitInit      = false;

    // ── Helpers ────────────────────────────────────────────────────────────
    function setNodeVis(vis: number): void {
      const s = String(vis);
      for (let i = 0; i < 30; i++) {
        noCircles[i].setAttribute('opacity', s);
        niCircles[i].setAttribute('opacity', s);
      }
    }
    function resetNodeRadii(): void {
      for (let i = 0; i < 30; i++) {
        noCircles[i].setAttribute('r', String(OUTER_R[i]));
        niCircles[i].setAttribute('r', String(INNER_R[i]));
      }
    }
    function setUeOpacity(op: number): void {
      const s = String(op);
      for (let j = 0; j < ueLines.length; j++) ueLines[j].setAttribute('stroke-opacity', s);
    }
    function setEiOpacity(op: number): void {
      const s = String(op);
      for (let j = 0; j < elLines.length; j++) elLines[j].setAttribute('stroke-opacity', s);
    }
    function setEiHiOpacity(op: number): void {
      const s = String(op);
      for (let j = 0; j < elHiLines.length; j++) elHiLines[j].setAttribute('stroke-opacity', s);
    }
    function moveNodes(t: number): void {
      for (let i = 0; i < 30; i++) {
        const cx = String(lerp(UP[i][0], FP[i][0], t));
        const cy = String(lerp(UP[i][1], FP[i][1], t));
        noCircles[i].setAttribute('cx', cx); noCircles[i].setAttribute('cy', cy);
        niCircles[i].setAttribute('cx', cx); niCircles[i].setAttribute('cy', cy);
      }
    }
    function moveEI(t: number): void {
      for (let j = 0; j < EI.length; j++) {
        const [a, b] = EI[j];
        const x1 = String(lerp(UP[a][0], FP[a][0], t));
        const y1 = String(lerp(UP[a][1], FP[a][1], t));
        const x2 = String(lerp(UP[b][0], FP[b][0], t));
        const y2 = String(lerp(UP[b][1], FP[b][1], t));
        elLines[j].setAttribute('x1', x1); elLines[j].setAttribute('y1', y1);
        elLines[j].setAttribute('x2', x2); elLines[j].setAttribute('y2', y2);
        elHiLines[j].setAttribute('x1', x1); elHiLines[j].setAttribute('y1', y1);
        elHiLines[j].setAttribute('x2', x2); elHiLines[j].setAttribute('y2', y2);
      }
    }
    function drawIcons(): void {
      if (!iconGroups) return;
      for (let i = 0; i < 30; i++) {
        const [cx, cy] = UP[i];
        iconGroups[i].setAttribute(
          'transform',
          `translate(${cx},${cy + ICON_Y_OFFSET}) scale(${iconScales[i] * ICON_SIZE})`
        );
      }
    }

    // ── Estado inicial ─────────────────────────────────────────────────────
    moveNodes(0);
    setNodeVis(0);
    resetNodeRadii();
    setUeOpacity(0);
    setEiOpacity(0);
    setEiHiOpacity(0);
    moveEI(0);
    for (let j = 0; j < UE.length; j++) {
      const [a, b] = UE[j];
      ueLines[j].setAttribute('x1', String(UP[a][0])); ueLines[j].setAttribute('y1', String(UP[a][1]));
      ueLines[j].setAttribute('x2', String(UP[b][0])); ueLines[j].setAttribute('y2', String(UP[b][1]));
    }
    if (iconGroups) {
      for (let i = 0; i < 30; i++) {
        const [cx, cy] = UP[i];
        iconGroups[i].setAttribute('transform', `translate(${cx},${cy + ICON_Y_OFFSET}) scale(0)`);
      }
    }

    let startTime: number | null = null;
    let rafId: number;

    function tick(now: number): void {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;

      // Fade-in global al arrancar
      svg.style.opacity = elapsed < T.FADE_IN ? String(elapsed / T.FADE_IN) : '1';

      const loop = elapsed % LOOP_DUR;

      // Límites de fase
      const L0 = T.NODES_ICONS_IN;
      const L1 = L0 + T.EDGES_IN;
      const L2 = L1 + T.ICONS_OUT;
      const L3 = L2 + T.HOLD_URBAN;
      const L4 = L3 + T.CONVERGE;
      const L5 = L4 + T.HOLD_W;
      const L6 = L5 + T.CONVERGE;
      // L7 = L6 + T.NET_FADEOUT = LOOP_DUR

      if (loop < L0) {
        // ── NODES_AND_ICONS_IN ────────────────────────────────────────────
        // Nodos + íconos aparecen juntos con bounce escalonado
        if (!nodeIconsInit) {
          for (let i = 0; i < 30; i++) nodeIconDelay[i] = Math.random() * STAGGER_MAX;
          nodeIconsInit = true;
        }
        edgesInit = false; exitInit = false;
        const ct = loop / T.NODES_ICONS_IN;
        moveNodes(0); moveEI(0);
        setUeOpacity(0); setEiOpacity(0); setEiHiOpacity(0);
        for (let i = 0; i < 30; i++) {
          const sc = bounceSettle(Math.max(0, Math.min(1, (ct - nodeIconDelay[i]) / ANIM_WINDOW)));
          // Nodo: opacidad y radio escalan juntos
          noCircles[i].setAttribute('opacity', sc > 0.01 ? '1' : '0');
          niCircles[i].setAttribute('opacity', sc > 0.01 ? '1' : '0');
          noCircles[i].setAttribute('r', String(OUTER_R[i] * sc));
          niCircles[i].setAttribute('r', String(INNER_R[i] * sc));
          iconScales[i] = sc;
        }
        drawIcons();

      } else if (loop < L1) {
        // ── EDGES_IN ──────────────────────────────────────────────────────
        // Segmentos se tejen; nodos e íconos ya a full; radios resetean
        if (!edgesInit) {
          resetNodeRadii();
          setNodeVis(1);
          for (let j = 0; j < UE.length; j++) edgeDelay[j] = Math.random() * EDGE_MAX_DELAY;
          edgesInit = true;
        }
        exitInit = false;
        const ct = (loop - L0) / T.EDGES_IN;
        moveNodes(0); moveEI(0); setEiOpacity(0); setEiHiOpacity(0);
        if (!hideUE) {
          for (let j = 0; j < ueLines.length; j++) {
            const d = edgeDelay[j];
            ueLines[j].setAttribute('stroke-opacity',
              String(0.42 * easeOutCubic(ct < d ? 0 : Math.min(1, (ct - d) / EDGE_ANIM_DUR))));
          }
        }
        for (let i = 0; i < 30; i++) iconScales[i] = 1;
        drawIcons();

      } else if (loop < L2) {
        // ── ICONS_OUT ─────────────────────────────────────────────────────
        // Íconos desaparecen con bounce; nodos y trama permanecen
        if (!exitInit) {
          for (let i = 0; i < 30; i++) exitDelay[i] = Math.random() * EXIT_MAX_DELAY;
          exitInit = true;
        }
        const ct = (loop - L1) / T.ICONS_OUT;
        moveNodes(0); moveEI(0);
        setNodeVis(1); setUeOpacity(hideUE ? 0 : 0.42); setEiOpacity(0); setEiHiOpacity(0);
        for (let i = 0; i < 30; i++) {
          iconScales[i] = ct < exitDelay[i]
            ? 1
            : bounceOut(Math.min(1, (ct - exitDelay[i]) / EXIT_ANIM_DUR));
        }
        drawIcons();

      } else if (loop < L3) {
        // ── HOLD_URBAN ────────────────────────────────────────────────────
        moveNodes(0); moveEI(0);
        setNodeVis(1); setUeOpacity(hideUE ? 0 : 0.42); setEiOpacity(0); setEiHiOpacity(0);
        for (let i = 0; i < 30; i++) iconScales[i] = 0;
        drawIcons();

      } else if (loop < L4) {
        // ── CONVERGE_FWD ─ ciudad → W ─────────────────────────────────────
        const ct = (loop - L3) / T.CONVERGE;
        const nt = easeInOut(ct);
        const ue = hideUE ? 0 : (ct < UE_FADE_END ? 0.42 * (1 - ct / UE_FADE_END) : 0);
        const ei = ct < EI_FADE_START ? 0 : Math.min(1, (ct - EI_FADE_START) / (1 - EI_FADE_START));
        moveNodes(nt); moveEI(nt);
        setNodeVis(1); setUeOpacity(ue); setEiOpacity(ei); setEiHiOpacity(0);
        for (let i = 0; i < 30; i++) iconScales[i] = 0;
        drawIcons();

      } else if (loop < L5) {
        // ── HOLD_W + HIGHLIGHT ────────────────────────────────────────────
        // Pulso de brillo en el perímetro de la W al completarse
        const ct = (loop - L4) / T.HOLD_W;
        moveNodes(1); moveEI(1);
        setNodeVis(1); setUeOpacity(0); setEiOpacity(1);
        // Highlight: flash rápido al inicio → mantiene → se desvanece
        let hiOp = 0;
        if      (ct < 0.10) hiOp = easeOutCubic(ct / 0.10);
        else if (ct < 0.65) hiOp = 1;
        else if (ct < 0.95) hiOp = 1 - easeOutCubic((ct - 0.65) / 0.30);
        else                hiOp = 0;
        setEiHiOpacity(hiOp);
        for (let i = 0; i < 30; i++) iconScales[i] = 0;
        drawIcons();

      } else if (loop < L6) {
        // ── CONVERGE_REV ─ W → ciudad ─────────────────────────────────────
        const ct = (loop - L5) / T.CONVERGE;
        const nt = easeInOut(1 - ct);
        const ei = ct < (1 - EI_FADE_START) ? 1 - ct / (1 - EI_FADE_START) : 0;
        const ue = hideUE ? 0 : (
          ct < (1 - UE_FADE_END) ? 0 : 0.42 * (ct - (1 - UE_FADE_END)) / UE_FADE_END
        );
        moveNodes(nt); moveEI(nt);
        setNodeVis(1); setUeOpacity(ue); setEiOpacity(ei); setEiHiOpacity(0);
        for (let i = 0; i < 30; i++) iconScales[i] = 0;
        drawIcons();

      } else {
        // ── NET_FADEOUT ───────────────────────────────────────────────────
        // Ciudad se disuelve; el siguiente loop arranca sobre negro
        nodeIconsInit = false;  // re-aleatoriza stagger en el próximo ciclo
        const ct = (loop - L6) / T.NET_FADEOUT;
        const vis = 1 - easeOutCubic(ct);
        moveNodes(0); moveEI(0);
        setNodeVis(vis); setUeOpacity(hideUE ? 0 : 0.42 * vis);
        setEiOpacity(0); setEiHiOpacity(0);
        for (let i = 0; i < 30; i++) iconScales[i] = 0;
        drawIcons();
      }

      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(rafId); svg.style.opacity = ''; };
  }, [hideUE, hideIcons, size]);

  return (
    <svg
      ref={svgRef}
      viewBox="110 260 1040 625"
      className={cls}
      focusable="false"
      aria-hidden="true"
      overflow="visible"
    >
      <defs>
        {/* Glow suave para aristas urbanas e íconos */}
        <filter id="unlv4-ue-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.8" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        {/* Glow medio para aristas W */}
        <filter id="unlv4-w-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.8" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        {/* Glow para nodos */}
        <filter id="unlv4-node-glow" x="-400%" y="-400%" width="900%" height="900%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="6"   result="b1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b2" />
          <feMerge>
            <feMergeNode in="b1" /><feMergeNode in="b2" /><feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Glow intenso para el highlight de la W al completarse */}
        <filter id="unlv4-w-hi-glow" x="-160%" y="-160%" width="420%" height="420%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="b1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="5"  result="b2" />
          <feMerge>
            <feMergeNode in="b1" /><feMergeNode in="b2" /><feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Aristas urbanas */}
      <g stroke="#2ab8e8" strokeWidth="0.9" fill="none" strokeLinecap="round" filter="url(#unlv4-ue-glow)">
        {UE.map((_, i) => <line key={i} className="unl__ue" x1="0" y1="0" x2="0" y2="0" />)}
      </g>

      {/* Aristas W — capa principal */}
      <g stroke="#2ab8e8" strokeWidth="1.4" fill="none" strokeLinecap="round" filter="url(#unlv4-w-glow)">
        {EI.map((_, i) => <line key={i} className="unl__el" x1="0" y1="0" x2="0" y2="0" />)}
      </g>

      {/* Aristas W — capa highlight (brillo al formarse) */}
      <g stroke="#c8f2ff" strokeWidth="2.4" fill="none" strokeLinecap="round" filter="url(#unlv4-w-hi-glow)">
        {EI.map((_, i) => <line key={i} className="unl__el-hi" x1="0" y1="0" x2="0" y2="0" />)}
      </g>

      {/* Nodos — aro exterior */}
      <g fill="#18a0d0" opacity={0.55} filter="url(#unlv4-node-glow)">
        {OUTER_R.map((r, i) => <circle key={i} className="unl__no" cx="627" cy="577" r={r} />)}
      </g>
      {/* Nodos — punto interior */}
      <g fill="#c8f2ff">
        {INNER_R.map((r, i) => <circle key={i} className="unl__ni" cx="627" cy="577" r={r} />)}
      </g>

      {/* Íconos — uno por nodo, 30 en total */}
      {!hideIcons && ICON_FOR_NODE.map((type, i) => (
        <g key={i} className="unl__icon"
           transform="translate(0,0) scale(0)"
           stroke="#2ab8e8" fill="none"
           strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round"
           filter="url(#unlv4-ue-glow)">
          <IconPaths type={type} />
        </g>
      ))}
    </svg>
  );
}
